import { useState, useEffect, useMemo, useCallback } from 'react';
import Container from '@/components/ui/Container';
import DashboardHeader from '@/components/dashboard/DashboardHeader';
import { ListTodo, ClipboardPlus } from 'lucide-react';
import MobilePageWrapper from '@/components/ui/MobilePageWrapper';
import { useQueryClient } from '@tanstack/react-query';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';

import TaskViewContainer from '@/components/tasks/TaskViewContainer';
import TaskDateNavigation from '@/components/tasks/components/TaskDateNavigation';
import { ViewMode } from '@/components/tasks/TaskViewContainer';
import { useTaskManagerOperations } from '@/hooks/tasks/use-task-manager-operations';
import { useCreateTaskWithAssignees } from '@/hooks/tasks/use-create-task-with-assignees';
import { DateRangeOption } from '@/hooks/tasks/use-task-operations';
import { useAuth } from '@/contexts/auth';

import GoalsSidebar from '@/components/task-manager/GoalsSidebar';
import DashboardCard from '@/components/ui/DashboardCard';
import TaskBoardContainer from '@/components/tasks/components/TaskBoardContainer';
import MobileSidebarAccordion from '@/components/ui/MobileSidebarAccordion';
import SidebarHeader from '@/components/ui/SidebarHeader';
import UnifiedCreationModal from '@/components/shared/UnifiedCreationModal';
import NewWorkbookModal from '@/components/content-generator/workbook/NewWorkbookModal';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAutoNavigation } from '@/hooks/nav/use-auto-navigation';
import useGoalsState from '@/hooks/goal-planner/useGoalsState';
import { useDebouncedValue } from '@/lib/utils';
import { getTaskViewPreference } from '@/utils/localStorage';
import { useModalTrigger } from '@/hooks/navigation/use-modal-trigger';
import { URL_PARAMS } from '@/config/url-parameters';
import { useIsMobile } from '@/hooks/layout/use-mobile';
import { Target } from 'lucide-react';

// Empty state imports
import { UniversalEmptyStateWrapper } from '@/components/shared/empty-states/UniversalEmptyStateWrapper';
import { EmptyStateLayout } from '@/components/shared/empty-states/EmptyStateLayout';
import { SimpleTaskForm } from '@/components/shared/empty-states/SimpleTaskForm';
import { getAppConfig } from '@/components/shared/empty-states/app-configs';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const TaskManager = () => {
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  
  // Empty state task creation
  const [isCreatingTask, setIsCreatingTask] = useState(false);
  
  // Listen for custom events to open task detail
  useEffect(() => {
    const handleOpenTaskEvent = (event: Event) => {
      const customEvent = event as CustomEvent;
      const taskId = customEvent.detail?.taskId;
      
      console.log('[TaskManager] Received open-task-detail event:', taskId);
      
      if (taskId) {
        console.log('[TaskManager] Setting editingTaskId:', taskId);
        setEditingTaskId(taskId);
      }
    };

    window.addEventListener('open-task-detail', handleOpenTaskEvent);
    
    return () => {
      window.removeEventListener('open-task-detail', handleOpenTaskEvent);
    };
  }, []);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { goToWorkbookEditor, goToTaskManager } = useAutoNavigation();
  const queryClient = useQueryClient();
  const { activeGoals, isLoading: isGoalsLoading } = useGoalsState();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  
  // URL parameter modal trigger detection
  const { shouldOpen: shouldOpenTaskModal } = useModalTrigger(URL_PARAMS.MODALS.CREATE_TASK);
  
  // Debounce search query
  const debouncedSearch = useDebouncedValue(searchQuery, 500);
  
  // 🤖 AI CONTEXT: Cache now populated by database-driven system in AuthProvider
  
  // Get the saved view preference from localStorage
  const savedViewPreference = getTaskViewPreference();

  // No redirect logic needed - handled by navbar buttons for snappy navigation
  
  // Use simple task operations for standalone task management
  const [dateRange, setDateRange] = useState<DateRangeOption>('week');
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  
  // Handle Now/All toggle state locally (removed global context dependency)
  const [showOnlyCurrentRange, setShowOnlyCurrentRange] = useState(false);
  
  const { 
    activeTasks: queryActiveTasks,
    completedTasks: queryCompletedTasks,
    addTask,
    completeTask,
    uncompleteTask,
    removeTask,
    updateTask,
    taskManagerOperations,
    isLoading,
    refetch: refetchTasks
  } = useTaskManagerOperations(dateRange, currentDate, showOnlyCurrentRange);
  
  // Local state for immediate drag updates (like project-manager pattern)
  const [localActiveTasks, setLocalActiveTasks] = useState<Task[]>([]);
  const [localCompletedTasks, setLocalCompletedTasks] = useState<Task[]>([]);
  const [useLocalState, setUseLocalState] = useState(false);
  
  // Update local state when React Query data changes (but only when not using local state)
  useEffect(() => {
    if (!useLocalState) {
      setLocalActiveTasks(queryActiveTasks);
      setLocalCompletedTasks(queryCompletedTasks);
    }
  }, [queryActiveTasks, queryCompletedTasks, useLocalState]);
  
  // Use local state during drag operations, otherwise use React Query data
  const activeTasks = useLocalState ? localActiveTasks : queryActiveTasks;
  const completedTasks = useLocalState ? localCompletedTasks : queryCompletedTasks;
  
  // Enhanced task manager operations that update local state immediately
  const enhancedTaskManagerOperations = useMemo(() => ({
    ...taskManagerOperations,
    updateTask: (taskId: string, updates: Partial<Task>, silent?: boolean) => {
      // Handle completion status changes (moving between lists)
      if (updates.isCompleted !== undefined) {
        const sourceList = updates.isCompleted ? localActiveTasks : localCompletedTasks;
        const targetList = updates.isCompleted ? localCompletedTasks : localActiveTasks;
        const sourceSetFunction = updates.isCompleted ? setLocalActiveTasks : setLocalCompletedTasks;
        const targetSetFunction = updates.isCompleted ? setLocalCompletedTasks : setLocalActiveTasks;
        
        const task = sourceList.find(t => t.id === taskId);
        if (task) {
          const updatedTask = { ...task, ...updates };
          sourceSetFunction(sourceList.filter(t => t.id !== taskId));
          targetSetFunction([...targetList, updatedTask]);
        }
      } else {
        // Handle updates within the same list
        const updateTaskInList = (tasks: Task[]) => 
          tasks.map(task => task.id === taskId ? { ...task, ...updates } : task);
        
        setLocalActiveTasks(updateTaskInList);
        setLocalCompletedTasks(updateTaskInList);
      }
      
      // Then update database via React Query
      taskManagerOperations.updateTask(taskId, updates, silent);
    },
    setLocalState: setUseLocalState,
    setLocalActiveTasks,
    setLocalCompletedTasks
  }), [taskManagerOperations, localActiveTasks, localCompletedTasks]);
  
  // Use robust project task creation for tasks with goalId
  const createTaskWithAssignees = useCreateTaskWithAssignees();
  
  // Handle URL parameter trigger for task modal
  useEffect(() => {
    if (shouldOpenTaskModal) {
      console.log('[TaskManager] URL parameter detected, opening task modal');
      setIsTaskModalOpen(true);
    }
  }, [shouldOpenTaskModal]);
  
  // Initialize with saved preference on component mount
  useEffect(() => {
    if (savedViewPreference && savedViewPreference !== dateRange) {
      setDateRange(savedViewPreference as DateRangeOption);
    }
  }, []);
  
  // Effect to retain tasks across view transitions
  useEffect(() => {
    // Ensure we have tasks data available
    if (!isLoading && (activeTasks.length === 0 && completedTasks.length === 0)) {
      refetchTasks();
    }
  }, [dateRange, currentDate, showOnlyCurrentRange, isLoading, activeTasks.length, completedTasks.length, refetchTasks]);
  
  // Memoize combined tasks to prevent unnecessary re-renders
  const allTasks = useMemo(() => {
    return [...activeTasks, ...completedTasks];
  }, [activeTasks, completedTasks]);

  // Create custom task operations object to avoid global context dependencies
  const customTaskOperations = useMemo(() => ({
    completeTask,
    uncompleteTask,
    removeTask,
    updateTask
  }), [completeTask, uncompleteTask, removeTask, updateTask]);
  
  // Memoized task count calculation for project filter
  const projectTaskCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    let totalActiveTasks = 0;
    
    allTasks.forEach(task => {
      // Only count active tasks (not completed)
      if (!task.isCompleted) {
        totalActiveTasks++;
        if (task.goalId) {
          // Find the project associated with this task's goal
          const project = activeGoals.find(goal => goal.id === task.goalId);
          if (project?.projectId) {
            counts[project.projectId] = (counts[project.projectId] || 0) + 1;
          }
        }
      }
    });
    
    return { projectCounts: counts, totalTasks: totalActiveTasks };
  }, [allTasks, activeGoals]);
  
  // Filter tasks based on search query and project filter
  const filteredTasks = useMemo(() => {
    let filtered = allTasks;
    
    // Apply search filter
    if (debouncedSearch) {
      const query = debouncedSearch.toLowerCase();
      filtered = filtered.filter(task =>
        task.title.toLowerCase().includes(query) ||
        (task.description && task.description.toLowerCase().includes(query))
      );
    }
    
    // Apply project filter
    if (selectedProjectId) {
      // Find the goal ID associated with the selected project
      const projectGoal = activeGoals.find(goal => goal.projectId === selectedProjectId);
      if (projectGoal) {
        filtered = filtered.filter(task => task.goalId === projectGoal.id);
      } else {
        // If no goal found for the project, show no tasks
        filtered = [];
      }
    }
    
    return filtered;
  }, [allTasks, debouncedSearch, selectedProjectId, activeGoals]);
  
  // Determine if we should show the centered empty state
  const hasAnyTasks = allTasks.length > 0;
  const shouldShowCenteredLayout = !isLoading && !hasAnyTasks && 
    !searchQuery && !selectedProjectId && !showOnlyCurrentRange;
  
  // Get task manager app config
  const taskManagerConfig = getAppConfig('task-manager');
  
  // Handle task creation from empty state
  const handleEmptyStateTaskCreation = useCallback(async (taskData: { title: string; dueDate?: string }) => {
    if (!user?.id || isCreatingTask) return;
    
    setIsCreatingTask(true);
    
    try {
      const { data, error } = await supabase.rpc('create_task_simple', {
        p_title: taskData.title,
        p_description: null,
        p_due_date: taskData.dueDate || null,
        p_priority: null,
        p_urgency: null
      });

      if (error) {
        throw new Error(error.message);
      }

      if (!data) {
        throw new Error('No response from server');
      }

      // Parse JSON response
      const result = typeof data === 'string' ? JSON.parse(data) : data;

      if (!result.success) {
        throw new Error(result.error || 'Task creation failed');
      }

      toast.success('Task created successfully!');
      
      // Refresh tasks to show the new task
      refetchTasks();
      
    } catch (error) {
      console.error('Task creation failed:', error);
      toast.error(`Failed to create task: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsCreatingTask(false);
    }
  }, [user?.id, isCreatingTask, refetchTasks]);

  const handleNewCreation = useCallback(() => {
    setIsTaskModalOpen(true);
  }, []);

  const handleCreateTaskFromEmpty = useCallback(() => {
    setIsTaskModalOpen(true);
  }, []);

  // Handle Create Project button from sidebar
  const handleCreateProjectFromSidebar = useCallback(() => {
    setIsProjectModalOpen(true);
  }, []);

  const handleViewChange = useCallback((view: ViewMode) => {
    // Convert ViewMode to DateRangeOption and preserve current tasks during transition
    setDateRange(view as DateRangeOption);
  }, [setDateRange]);
  
  // Handle Now/All toggle
  const handleShowOnlyCurrentRangeChange = useCallback((value: boolean) => {
    setShowOnlyCurrentRange(value);
  }, [setShowOnlyCurrentRange]);
  
  // Handle date changes from the TaskViewContainer
  const handleDateChange = useCallback((newDate: Date) => {
    setCurrentDate(newDate);
  }, [setCurrentDate]);

  // Generate header text based on current view mode and date
  const getHeaderText = useCallback(() => {
    switch (dateRange) {
      case 'day':
        return format(currentDate, 'EEEE, MMMM d');
      case 'week':
        const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
        const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
        return `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d')}`;
      case 'month':
        return format(currentDate, 'MMMM yyyy');
      default:
        return '';
    }
  }, [currentDate, dateRange]);

  // Date navigation handlers
  const handlePrevious = useCallback(() => {
    let newDate: Date;
    switch (dateRange) {
      case 'day':
        newDate = new Date(currentDate);
        newDate.setDate(newDate.getDate() - 1);
        break;
      case 'week':
        newDate = new Date(currentDate);
        newDate.setDate(newDate.getDate() - 7);
        break;
      case 'month':
        newDate = new Date(currentDate);
        newDate.setMonth(newDate.getMonth() - 1);
        break;
      default:
        newDate = currentDate;
    }
    setCurrentDate(newDate);
  }, [currentDate, dateRange]);

  const handleNext = useCallback(() => {
    let newDate: Date;
    switch (dateRange) {
      case 'day':
        newDate = new Date(currentDate);
        newDate.setDate(newDate.getDate() + 1);
        break;
      case 'week':
        newDate = new Date(currentDate);
        newDate.setDate(newDate.getDate() + 7);
        break;
      case 'month':
        newDate = new Date(currentDate);
        newDate.setMonth(newDate.getMonth() + 1);
        break;
      default:
        newDate = currentDate;
    }
    setCurrentDate(newDate);
  }, [currentDate, dateRange]);

  const handleToday = useCallback(() => {
    setCurrentDate(new Date());
  }, []);

  // Handle task creation - conditional logic based on project selection
  const handleTaskCreated = useCallback(async (taskData: any) => {
    try {
      if (taskData.goalId) {
        // Project task - use robust function with project validation and features
        console.log('[TaskManager] Creating project task with goalId:', taskData.goalId);
        await createTaskWithAssignees.mutateAsync({
          title: taskData.title,
          goalId: taskData.goalId,
          description: taskData.description,
          dueDate: taskData.dueDate,
          priority: taskData.priority,
          assigneeIds: user?.id ? [user.id] : [] // Auto-assign creator for TaskManager visibility
        });
        
        // Refresh tasks to show the new project task
        refetchTasks();
      } else {
        // Standalone task - UnifiedCreationModal already created it, just refresh the list
        console.log('[TaskManager] Standalone task already created by modal, refreshing task list');
        refetchTasks();
      }
    } catch (error) {
      console.error('[TaskManager] Task creation failed:', error);
      // Error toast is handled by the respective hooks
    }
  }, [createTaskWithAssignees, refetchTasks, user?.id]);

  // Handle project creation (from NewWorkbookModal)
  const handleProjectCreated = useCallback((projectId: string, goalId: string) => {
    // Force refetch of goals to update the sidebar
    queryClient.invalidateQueries({ queryKey: ['goals'] });
    // Navigate to the new project route structure
    goToTaskManager(goalId, projectId);
  }, [queryClient, goToTaskManager]);

  // Handle modal close with refetch
  const handleProjectModalClose = useCallback(() => {
    setIsProjectModalOpen(false);
    // Force refetch of goals to update the sidebar
    queryClient.invalidateQueries({ queryKey: ['goals'] });
  }, [queryClient]);

  // Handle search changes
  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  // Handle project filter changes
  const handleProjectFilterChange = useCallback((projectId: string | null) => {
    setSelectedProjectId(projectId);
  }, []);

  return (
    <MobilePageWrapper>
      <Container>
        {shouldShowCenteredLayout ? (
          <UniversalEmptyStateWrapper 
            isLoading={isLoading}
            hasData={hasAnyTasks}
            title={taskManagerConfig.title}
            description={taskManagerConfig.description}
            icon={<taskManagerConfig.icon className="h-6 w-6" />}
            sidebar={<GoalsSidebar onCreateGoal={handleCreateProjectFromSidebar} />}
            emptyStateContent={
              <EmptyStateLayout 
                icon={<taskManagerConfig.icon className={`h-8 w-8 ${taskManagerConfig.iconColor}`} />}
                title={taskManagerConfig.emptyStateTitle}
                variant={taskManagerConfig.variant}
              >
                <SimpleTaskForm 
                  onSubmit={handleEmptyStateTaskCreation}
                  isLoading={isCreatingTask}
                />
              </EmptyStateLayout>
            }
          >
            {/* This content won't be shown in empty state but required for interface */}
            <div />
          </UniversalEmptyStateWrapper>
        ) : (
          <>
            <DashboardHeader 
              title="Task Manager"
              description="Organize and track your strategic initiatives"
              icon={<ListTodo className="h-6 w-6" />}
            />
            
            <div className="space-y-3 md:space-y-6">
              {/* Two-Column Layout - Tasks left, Projects sidebar right on desktop. Accordion on mobile */}
              <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-3 md:gap-6 min-w-0">
                {/* Left Column - Tasks */}
                <div className="space-y-3 md:space-y-6 order-1 lg:order-1 min-w-0">
                  {/* Task board container moved outside the card - animation removed */}
                  <TaskBoardContainer
                    tasks={filteredTasks}
                    viewMode={dateRange as ViewMode}
                    currentDate={currentDate}
                    isTransitioning={false}
                    isLoading={isLoading}
                    onAddTask={addTask}
                    onCreateTask={handleCreateTaskFromEmpty}
                    customTaskOperations={customTaskOperations}
                    taskManagerOperations={enhancedTaskManagerOperations}
                    projectTaskCounts={projectTaskCounts}
                    onProjectFilterChange={handleProjectFilterChange}
                    selectedProjectId={selectedProjectId}
                    allTasks={allTasks}
                    editingTaskId={editingTaskId}
                    onCloseEdit={() => setEditingTaskId(null)}
                  />
                </div>

                {/* Right Column - Projects Sidebar with Accordion */}
                <div className="space-y-3 md:space-y-6 order-0 lg:order-2 min-w-0">
                  <MobileSidebarAccordion
                    variant="purple"
                    persistKey="task-manager-projects-sidebar"
                    defaultExpanded={!isMobile}
                    className="h-full"
                  >
                    <SidebarHeader 
                      title="Projects" 
                      icon={Target} 
                      variant="purple"
                    />
                    
                    <GoalsSidebar onCreateGoal={handleCreateProjectFromSidebar} />
                  </MobileSidebarAccordion>
                </div>
              </div>
            </div>
          </>
        )}

        <UnifiedCreationModal 
          isOpen={isTaskModalOpen}
          onClose={() => setIsTaskModalOpen(false)}
          onTaskCreated={handleTaskCreated}
          defaultTab="task"
          hideTabs={true}
          context="task-manager"
        />

        <NewWorkbookModal
          isOpen={isProjectModalOpen}
          onClose={handleProjectModalClose}
          onProjectCreated={handleProjectCreated}
        />
      </Container>
    </MobilePageWrapper>
  );
};

export default TaskManager; 